// Orders list — paginated view of production.orders via mcp.listOrders.
//
// Filters (season + status) are stored in URL search params so the view is
// shareable / refreshable. Clicking a row navigates to /orders/:id.

import { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { Loader2, AlertCircle, Plus } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LastEditedBy } from "@/components/LastEditedBy";
import { PermissionGate } from "@/components/PermissionGate";
import { AttentionSummary, FlowCell } from "@/components/flow/FlowBadges";
import { useMcp, mcp, type OrderRow, type OrderStatus } from "@/lib/mcp";
import { PRODUCTION_ORDERS_STATUS } from "@/lib/db-enums.generated";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useListStateRestore } from "@/hooks/useListStateRestore";
import { withFrom } from "@/lib/nav";

// Season filter is hydrated dynamically from production.seasons via
// mcp.listSeasons (see useMcp call in component). Status options come
// straight from the production.orders.status CHECK constraint via
// db-enums.generated.ts. Empty leading "" entry is the "All" sentinel.
const STATUSES: Array<"" | OrderStatus> = [
  "" as const,
  ...(PRODUCTION_ORDERS_STATUS as unknown as readonly OrderStatus[]),
];

function fmtZAR(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency", currency: "ZAR", maximumFractionDigits: 0,
  }).format(v);
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "numeric" });
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const tone =
    status === "in_production" ? "bg-blue-100 text-blue-900" :
    status === "shipped" ? "bg-indigo-100 text-indigo-900" :
    status === "delivered" ? "bg-green-100 text-green-900" :
    status === "cancelled" ? "bg-red-100 text-red-900" :
    status === "closed" ? "bg-muted text-muted-foreground" :
    "bg-amber-100 text-amber-900";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status.replace("_", " ")}
    </span>
  );
}

export function OrdersListPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const listKey = location.pathname + location.search;
  useListStateRestore({ key: listKey });
  const season = params.get("season") ?? "";
  const status = (params.get("status") ?? "") as "" | OrderStatus;

  const args = useMemo(() => ({
    ...(season ? { season } : {}),
    ...(status ? { status } : {}),
    limit: 100,
  }), [season, status]);

  const { data, error, loading, refresh } = useMcp(() => mcp.listOrders(args), [season, status]);

  useRealtimeRefresh(
    [{ schema: "production", table: "orders" }],
    refresh,
  );
  const { data: seasonsData } = useMcp(
    () => mcp.listSeasons({ include_archived: true }),
    [],
  );
  const seasonOptions = useMemo(
    () => ["", ...((seasonsData?.seasons ?? []).map((s) => s.code))],
    [seasonsData],
  );

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Orders</h2>
          <p className="text-sm text-muted-foreground">
            Production orders across all seasons and customers.
          </p>
        </div>
        <PermissionGate surface="/orders">
          <Button asChild size="sm">
            <Link to="/orders/new">
              <Plus className="h-4 w-4" /> New order
            </Link>
          </Button>
        </PermissionGate>
      </div>

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          {/* Orders has no page-tab row to badge (its status control is a
              <select>), so the missing-item / stalled-in-status count rides the
              existing Filters header instead — #984's documented option (a).
              Scoped to the loaded page, and the badge's title says so. */}
          <CardTitle className="flex items-center gap-2 text-base">
            Filters
            <AttentionSummary rows={data?.orders ?? []} />
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1 text-sm">
              <span className="text-muted-foreground">Season:</span>
              <select
                value={season}
                onChange={(e) => updateFilter("season", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm"
              >
                {seasonOptions.map((s) => (
                  <option key={s || "_all"} value={s}>{s || "All"}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-sm">
              <span className="text-muted-foreground">Status:</span>
              <select
                value={status}
                onChange={(e) => updateFilter("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s ? s.replace("_", " ") : "All"}</option>
                ))}
              </select>
            </label>
            {(season || status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setParams({}, { replace: true })}
              >
                Clear
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading orders…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Failed to load orders</p>
                <p className="text-xs opacity-80">{error.message}</p>
              </div>
            </div>
          )}
          {!loading && !error && data && (
            <OrdersTable orders={data.orders} listKey={listKey} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OrdersTable({ orders, listKey }: { orders: OrderRow[]; listKey: string }) {
  if (!orders || orders.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No orders match these filters.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="py-2 pr-3 font-medium">Ref</th>
            <th className="py-2 pr-3 font-medium">PO Number</th>
            <th className="py-2 pr-3 font-medium">Customer</th>
            <th className="py-2 pr-3 font-medium">Supplier</th>
            <th className="py-2 pr-3 font-medium">Season</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 pr-3 font-medium">Flow</th>
            <th className="py-2 pr-3 font-medium">Target Ex-Factory</th>
            <th className="py-2 pr-3 font-medium text-right">Total Value</th>
            <th className="py-2 pr-3 font-medium">Last edited</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr
              key={o.id}
              className="border-b last:border-b-0 transition-colors hover:bg-muted/40"
            >
              <td className="py-2 pr-3">
                <Link
                  to={withFrom(`/orders/${o.id}`, listKey)}
                  className="font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {o.display_id ?? "—"}
                </Link>
              </td>
              <td className="py-2 pr-3">
                <Link to={withFrom(`/orders/${o.id}`, listKey)} className="font-medium text-primary hover:underline">
                  {o.po_number ?? o.id.slice(0, 8)}
                </Link>
              </td>
              <td className="py-2 pr-3">{o.customer_name ?? "—"}</td>
              <td className="py-2 pr-3">{o.supplier_name ?? "—"}</td>
              <td className="py-2 pr-3">{o.season_code ?? o.season ?? "—"}</td>
              <td className="py-2 pr-3"><StatusBadge status={o.status} /></td>
              <td className="py-2 pr-3 whitespace-nowrap"><FlowCell row={o} /></td>
              <td className="py-2 pr-3">{fmtDate(o.target_ex_factory_date)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{fmtZAR(o.total_value_zar)}</td>
              <td className="py-2 pr-3">
                <LastEditedBy updated_at={o.updated_at} updated_by={o.updated_by} agent_id={o.agent_id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
